import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  useTheme,
  CircularProgress,
  Button,
  LinearProgress,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { CheckCircle as CheckCircleIcon } from '@mui/icons-material';
import { Schedule as ScheduleIcon } from '@mui/icons-material';
import { Person as PersonIcon } from '@mui/icons-material';
import { RemoveRedEyeOutlined as RemoveRedEyeOutlinedIcon } from '@mui/icons-material';
import { CelebrationOutlined as CelebrationOutlinedIcon } from '@mui/icons-material';
import { useGlobalConfig } from 'context/GlobalContext';
import { useLocation, useNavigate } from 'react-router-dom';
import { ROUTE_TREE } from 'routes/AppRoutes';
import { PilotApis } from 'apis/pilotAPI';
import { CustomerDataApis } from 'apis/customerDataAPIs';
import { useSelector } from 'react-redux';
import { selectSelectedPilotId, selectPilotList } from 'store/dataReducers/pilotsSlice';
import { toast } from 'react-toastify';

interface TaskStatus {
  uuidAssignment: 'not_started' | 'in_progress' | 'completed' | 'error';
  assignShcCluster: 'not_started' | 'in_progress' | 'completed' | 'error';
  assignPhysicalCluster: 'not_started' | 'in_progress' | 'completed' | 'error';
  tagAsTestUser: 'not_started' | 'in_progress' | 'completed' | 'error';
  setupDataSync: 'not_started' | 'in_progress' | 'completed' | 'error';
}

interface TaskProgress {
  completed: number;
  total: number;
  percentage: number;
}

export const UserCreationProgress = () => {
  const { rc } = useGlobalConfig();
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  
  const selectedPilotId = useSelector(selectSelectedPilotId);
  const pilotList = useSelector(selectPilotList);
  
  const [taskStatus, setTaskStatus] = useState<TaskStatus>({
    uuidAssignment: 'not_started',
    assignShcCluster: 'not_started',
    assignPhysicalCluster: 'not_started',
    tagAsTestUser: 'not_started',
    setupDataSync: 'not_started'
  });
  
  const [progress, setProgress] = useState<TaskProgress>({
    completed: 0,
    total: 1,
    percentage: 0
  });
  
  const [completedUsers, setCompletedUsers] = useState(0);
  const [totalUsers, setTotalUsers] = useState(5); // Default, can be made dynamic
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uuidData, setUuidData] = useState<any>(null);
  const [userDetailsModalOpen, setUserDetailsModalOpen] = useState(false);
  const [userDetails, setUserDetails] = useState<any>(null);
  const [userDetailsLoading, setUserDetailsLoading] = useState(false);
  const [shcClusterError, setShcClusterError] = useState<string | null>(null);

  // Get user ingestion data from localStorage
  const userIngestionDataStr = localStorage.getItem('userIngestionData');
  let userIngestionData = null;
  let uuids: string[] = [];
  let env = '';

  if (userIngestionDataStr) {
    try {
      userIngestionData = JSON.parse(userIngestionDataStr);
      uuids = userIngestionData.stagedUuids || [];
      env = userIngestionData.selectedEnvironment || 'NA';
    } catch (parseError) {
      console.error('Failed to parse user ingestion data from localStorage:', parseError);
    }
  }

  const totalUserCount = uuids.length || 5;

  useEffect(() => {
    if (!selectedPilotId || pilotList.length === 0) {
      console.log('Waiting for pilot data to load...');
      return;
    }
    
    if (!userIngestionDataStr || uuids.length === 0) {
      console.warn('Missing user ingestion data. Cannot proceed without user data.');
      setError('Missing user data. Please go back and select users again.');
      return;
    }
    
    setTotalUsers(totalUserCount);
    startUserCreationProcess();
  }, [totalUserCount, selectedPilotId, pilotList.length, userIngestionDataStr]);

  const startUserCreationProcess = async () => {
    setIsLoading(true);
    setError(null);

    try {
      if (!selectedPilotId) {
        setError('No pilot selected. Waiting for pilot data to load...');
        setIsLoading(false);
        return;
      }
      
      if (pilotList.length === 0) {
        setError('Pilot list not loaded. Waiting for pilot data to load...');
        setIsLoading(false);
        return;
      }
      
      if (!userIngestionDataStr || uuids.length === 0) {
        setError('User data not found. Please go back and select users again.');
        setIsLoading(false);
        return;
      }
      
      // Task 1: UUID Assignment - Call fetchUserToken API
      setTaskStatus(prev => ({ ...prev, uuidAssignment: 'in_progress' }));
      
      // Get the selected pilot to extract destination environment
      const selectedPilot = pilotList.find(pilot => String(pilot.projectId) === String(selectedPilotId));
      if (!selectedPilot) {
        setError('Selected pilot not found. Waiting for pilot data to load...');
        setIsLoading(false);
        return;
      }

      // Extract destination environment from utility name (e.g., "UAT" from "SORGENIA_UAT")
      const destinationEnv = (selectedPilot as any).environment?.environment_name?.split('_')?.slice(-1)?.[0] || '';
      
      const userTokenResponse = await PilotApis.fetchUserUUID(rc, {
        destinationPilotId: selectedPilotId?.toString() || '',
        destinationEnv: destinationEnv,
        sourceEnv: env,
        sourceUUID: uuids.join(',')
      });

      if (userTokenResponse.success) {
        setUuidData(userTokenResponse.data);
        setTaskStatus(prev => ({ ...prev, uuidAssignment: 'completed' }));
        
        // Update progress after UUID assignment completion
        updateProgress(1);
        
        // Task 2: Assign SHC Cluster - Temporarily disabled until backend API is ready
        setTaskStatus(prev => ({ ...prev, assignShcCluster: 'in_progress' }));
        
        try {
          
          
          let generatedUuids: string[] = [];
          
          if (userTokenResponse.data.results && Array.isArray(userTokenResponse.data.results)) {
            generatedUuids = userTokenResponse.data.results.map((result: any) => result.uuid).filter(Boolean);
          }
          else if (userTokenResponse.data.uuid) {
            generatedUuids = [userTokenResponse.data.uuid];
          }
          else if (Array.isArray(userTokenResponse.data)) {
            generatedUuids = userTokenResponse.data.map((item: any) => item.uuid).filter(Boolean);
          }
          else if (userTokenResponse.data.data && Array.isArray(userTokenResponse.data.data)) {
            generatedUuids = userTokenResponse.data.data.map((item: any) => item.uuid).filter(Boolean);
          }
          
          
          if (generatedUuids.length === 0) {
            const responseString = JSON.stringify(userTokenResponse.data);
            const uuidRegex = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;
            const foundUuids = responseString.match(uuidRegex);
            
            if (foundUuids && foundUuids.length > 0) {
              generatedUuids = foundUuids;
            } else {
              throw new Error('No UUIDs generated from the API response');
            }
          }
          
          const uuidStringForShcCluster = generatedUuids.join(',');
          
          const shcClusterResponse = await assignShcCluster(uuidStringForShcCluster, selectedPilot);
          
          if (shcClusterResponse.success) {
            setTaskStatus(prev => ({ ...prev, assignShcCluster: 'completed' }));
            setShcClusterError(null);
            updateProgress(2);
          } else {
            setTaskStatus(prev => ({ ...prev, assignShcCluster: 'error' }));
            
            // Extract error message from response payload
            let errorMessage = 'SHC cluster assignment failed';
            
            if (shcClusterResponse.data && typeof shcClusterResponse.data === 'object') {
              if (shcClusterResponse.data.message) {
                errorMessage = shcClusterResponse.data.message;
              } else if (shcClusterResponse.data.error?.message) {
                errorMessage = shcClusterResponse.data.error.message;
              }
            }
            
            if (errorMessage === 'SHC cluster assignment failed' && shcClusterResponse.error) {
              if (typeof shcClusterResponse.error === 'object' && shcClusterResponse.error !== null) {
                const errorObj = shcClusterResponse.error as any;
                if (errorObj.message) {
                  errorMessage = errorObj.message;
                } else if (errorObj.error?.message) {
                  errorMessage = errorObj.error.message;
                }
              } else if (typeof shcClusterResponse.error === 'string') {
                if (shcClusterResponse.error.includes('Request failed with status code')) {
                  errorMessage = 'SHC cluster assignment failed';
                } else {
                  errorMessage = shcClusterResponse.error;
                  if (errorMessage.includes('401') || errorMessage.includes('unauthorized')) {
                    errorMessage = 'Authentication failed for SHC cluster assignment. Please check your credentials and try again.';
                  } else if (errorMessage.includes('403')) {
                    errorMessage = 'Access denied for SHC cluster assignment. Please check your permissions.';
                  } else if (errorMessage.includes('404')) {
                    errorMessage = 'SHC cluster service not found. Please verify the configuration.';
                  } else if (errorMessage.includes('500')) {
                    errorMessage = 'Server error during SHC cluster assignment. Please try again later.';
                  } else if (errorMessage.includes('timeout') || errorMessage.includes('Timeout')) {
                    errorMessage = 'SHC cluster assignment timed out. Please check your connection and try again.';
                  } else if (errorMessage.includes('network') || errorMessage.includes('Network')) {
                    errorMessage = 'Network error during SHC cluster assignment. Please check your connection.';
                  }
                }
              }
            }
            
            setShcClusterError(errorMessage);
            console.warn('SHC cluster assignment failed:', errorMessage);
            toast.error(errorMessage);
            updateProgress(2);
          }

          // Task 3: Assign Physical Cluster (proceed even if previous failed)
          setTaskStatus(prev => ({ ...prev, assignPhysicalCluster: 'in_progress' }));
          try {
            const extractPhysicalClusterErrorMessage = (settledResults: PromiseSettledResult<any>[]): string | null => {
              const messages: string[] = [];
              settledResults.forEach((r: any) => {
                if (r.status === 'fulfilled') {
                  const v = r.value;
                  if (v && v.success === false) {
                    if (v.data && v.data.message) messages.push(v.data.message);
                    if (v.error) messages.push(v.error);
                  }
                } else if (r.status === 'rejected') {
                  const reason = r.reason || {};
                  const candidate = reason?.response?.data?.message || reason?.message || reason?.error?.message;
                  if (candidate) messages.push(candidate);
                }
              });
              return messages.length > 0 ? messages[0] : null;
            };

            const physResults = await Promise.allSettled(
              generatedUuids.map(uuid => CustomerDataApis.assignPhysicalCluster(rc, {
                uuid,
                destination_pilot_id: String(selectedPilotId)
              }))
            );
            
            const anySuccess = physResults.some(r => r.status === 'fulfilled' && (r as any).value?.success);
            
            if (!anySuccess) {
              await new Promise(resolve => setTimeout(resolve, 2000));
              const physResultsLatest = await Promise.allSettled(
                generatedUuids.map(uuid => CustomerDataApis.assignPhysicalCluster(rc, {
                  uuid,
                  destination_pilot_id: String(selectedPilotId)
                }))
              );
              const anySuccessLatest = physResultsLatest.some(r => r.status === 'fulfilled' && (r as any).value?.success);
              setTaskStatus(prev => ({ ...prev, assignPhysicalCluster: anySuccessLatest ? 'completed' : 'error' }));
              if (!anySuccessLatest) {
                const backendMsg = extractPhysicalClusterErrorMessage(physResultsLatest) || extractPhysicalClusterErrorMessage(physResults) || null;
                toast.error(backendMsg || 'Assign Physical Cluster failed. Please retry.');
              }
            } else {
              setTaskStatus(prev => ({ ...prev, assignPhysicalCluster: 'completed' }));
            }
            
            updateProgress(3);
          } catch (e) {
            setTaskStatus(prev => ({ ...prev, assignPhysicalCluster: 'error' }));
            const fallbackMsg = (e as any)?.response?.data?.message || (e as any)?.message;
            toast.error(fallbackMsg || 'Assign Physical Cluster failed due to an unexpected error.');
          }
        } catch (shcError: any) {
          setTaskStatus(prev => ({ ...prev, assignShcCluster: 'error' }));
          
          let errorMessage = 'SHC cluster assignment failed';
          
          if (shcError.response?.data?.message) {
            errorMessage = shcError.response.data.message;
          } else if (shcError.message) {
            errorMessage = shcError.message;
            
            if (shcError.message.includes('401') || shcError.message.includes('unauthorized')) {
              errorMessage = 'Authentication failed for SHC cluster assignment. Please check your credentials and try again.';
            } else if (shcError.message.includes('403')) {
              errorMessage = 'Access denied for SHC cluster assignment. Please check your permissions.';
            } else if (shcError.message.includes('404')) {
              errorMessage = 'SHC cluster service not found. Please verify the configuration.';
            } else if (shcError.message.includes('500')) {
              errorMessage = 'Server error during SHC cluster assignment. Please try again later.';
            } else if (shcError.message.includes('timeout') || shcError.message.includes('Timeout')) {
              errorMessage = 'SHC cluster assignment timed out. Please check your connection and try again.';
            } else if (shcError.message.includes('network') || shcError.message.includes('Network')) {
              errorMessage = 'Network error during SHC cluster assignment. Please check your connection.';
            }
          }
          
          setShcClusterError(errorMessage);
          console.error('SHC cluster assignment error:', errorMessage);
          toast.error(errorMessage);
          
          updateProgress(2);
        }
        
        setCompletedUsers(totalUsers);
      } else {
        setTaskStatus(prev => ({ ...prev, uuidAssignment: 'error' }));
        throw new Error(`UUID assignment failed: ${userTokenResponse.error}`);
      }

      // Task 3: Tag as Test User - Keep as upcoming
    //   setTaskStatus(prev => ({ ...prev, tagAsTestUser: 'not_started' }));

      // Task 4: Setup Daily Data Sync - Keep as upcoming
    //   setTaskStatus(prev => ({ ...prev, setupDataSync: 'not_started' }));

    } catch (error: any) {
      console.error('Error during user creation process:', error);
      const errorMessage = error.message || 'User creation process failed';
      setError(errorMessage);
      
      if (errorMessage.includes('not found') || 
          errorMessage.includes('not loaded') || 
          errorMessage.includes('not selected') ||
          errorMessage.includes('Missing')) {
      }
    } finally {
      setIsLoading(false);
    }
  };

  const assignShcCluster = async (uuid: string, selectedPilot: any): Promise<{ success: boolean; data?: any; error?: string }> => {
    try {
      const api_host = (selectedPilot as any).api_host || undefined;
      const destination_pilot_id = selectedPilot.projectId;
      
      if (!destination_pilot_id) {
        return {
          success: false,
          error: 'Missing required pilot configuration (projectId)'
        };
      }

      const requestData: {
        uuid: string;
        data_server_url?: string;
        destination_pilot_id: string;
      } = {
        uuid: uuid,
        destination_pilot_id: destination_pilot_id.toString()
      };

      if (api_host) {
        requestData.data_server_url = api_host;
      }
      
      const response = await PilotApis.assignShcCluster(rc, requestData);
      
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to assign SHC cluster'
      };
    }
  };

  const updateProgress = (completedTasks: number) => {
    const total = 3;
    const percentage = Math.round((completedTasks / total) * 100);
    setProgress({
      completed: completedTasks,
      total,
      percentage
    });
  };

  const handleBackToManageUsers = () => {
    navigate(ROUTE_TREE.PROJECT.CUSTOMER_DATA_GENERATOR.path, { 
      replace: true 
    });
  };

  const handleViewCreatedUsers = () => {
    setUserDetailsModalOpen(true);
    fetchUserMetadata();
  };

  const handleFinish = () => {
    // Clear localStorage data when finishing
    localStorage.removeItem('userIngestionData');
    navigate(ROUTE_TREE.PROJECT.CUSTOMER_DATA_GENERATOR.path, { 
      replace: true 
    });
  };

  const fetchUserMetadata = async () => {
    // Handle both old single UUID format and new multiple UUIDs format
    const hasUuidData = uuidData?.uuid || (uuidData?.results && uuidData.results.length > 0);
    
    if (!hasUuidData) {
      console.error('No UUID data available to fetch user metadata');
      return;
    }

    setUserDetailsLoading(true);
    try {
      const response = await PilotApis.getAllUserMetadata(rc, selectedPilotId?.toString());
      
      if (response.success && response.data) {
        const userDataArray = Array.isArray(response.data) ? response.data : 
                             response.data.data ? response.data.data : [];
        
        // Handle multiple UUIDs from the new response format
        if (uuidData?.results && Array.isArray(uuidData.results)) {
          // Find all users that match the generated UUIDs
          const generatedUuids = uuidData.results.map((result: any) => result.uuid);
          const matchingUsers = userDataArray.filter((user: any) => 
            generatedUuids.includes(user.uuid)
          );
          
          if (matchingUsers.length > 0) {
            setUserDetails(matchingUsers); // Set all matching users
          } else {
            console.warn('No users found matching the generated UUIDs');
            setUserDetails(null);
          }
        } else {
          // Fallback to old single UUID format
          const specificUser = userDataArray.find((user: any) => user.uuid === uuidData.uuid);
          
          if (specificUser) {
            setUserDetails(specificUser);
          } else {
            console.warn('User not found in metadata');
            setUserDetails(null);
          }
        }
      } else {
        console.error('Failed to fetch user metadata:', response.error);
        setUserDetails(null);
      }
    } catch (error) {
      console.error('Error fetching user metadata:', error);
      setUserDetails(null);
    } finally {
      setUserDetailsLoading(false);
    }
  };

  const handleCloseModal = () => {
    setUserDetailsModalOpen(false);
    setUserDetails(null);
  };

  const getTaskIcon = (status: 'not_started' | 'in_progress' | 'completed' | 'error') => {
    switch (status) {
      case 'completed':
        return <CheckCircleIcon sx={{ fontSize: 32, color: theme.palette.success.main }} />;
      case 'in_progress':
        return <CircularProgress size={32} sx={{ color: theme.palette.info.main }} />;
      case 'error':
        return <Typography color="error" variant="h4">✗</Typography>;
      case 'not_started':
        return <Typography color="text.secondary" variant="h4">⚪</Typography>;
    }
  };

  const getTaskBackgroundColor = (status: 'not_started' | 'in_progress' | 'completed' | 'error') => {
    switch (status) {
      case 'completed':
        return theme.palette.success.light + '20'; 
      case 'in_progress':
        return theme.palette.info.light + '20';
      case 'error':
        return theme.palette.error.light + '20'; 
      case 'not_started':
        return theme.palette.background.paper; 
    }
  };

  const getTaskBorderColor = (status: 'not_started' | 'in_progress' | 'completed' | 'error') => {
    switch (status) {
      case 'completed':
        return theme.palette.success.main;
      case 'in_progress':
        return theme.palette.info.main;
      case 'error':
        return theme.palette.error.main;
      case 'not_started':
        return theme.palette.divider;
    }
  };

  const getStatusText = (status: 'not_started' | 'in_progress' | 'completed' | 'error') => {
    switch (status) {
      case 'completed':
        return 'Completed';
      case 'in_progress':
        return 'In Progress...';
      case 'error':
        return 'Failed';
      case 'not_started':
        return 'Upcoming';
    }
  };

  const getStatusColor = (status: 'not_started' | 'in_progress' | 'completed' | 'error') => {
    switch (status) {
      case 'completed':
        return theme.palette.success.main;
      case 'in_progress':
        return theme.palette.info.main;
      case 'error':
        return theme.palette.error.main;
      case 'not_started':
        return theme.palette.text.secondary;
    }
  };

  const tasks = [
    {
      key: 'uuidAssignment',
      title: 'UUID Assignment',
      description: 'Generate and assign a unique UUID once the test user is created in the target project.',
      icon: <PersonIcon sx={{ fontSize: 24, color: theme.palette.text.secondary }} />,
      status: taskStatus.uuidAssignment
    },
    {
      key: 'assignShcCluster',
      title: 'Assign SHC Cluster',
      description: 'Assign the user to the appropriate SHC (Smart Home Cluster) based on available clusters and user distribution.',
      icon: <CheckCircleIcon sx={{ fontSize: 24, color: theme.palette.text.secondary }} />,
      status: taskStatus.assignShcCluster
    },
    {
      key: 'assignPhysicalCluster',
      title: 'Assign Physical Cluster',
      description: 'Assign the user to a DCX Physical Cluster in the destination project.',
      icon: <CheckCircleIcon sx={{ fontSize: 24, color: theme.palette.text.secondary }} />,
      status: taskStatus.assignPhysicalCluster
    }
    // {
    //   key: 'tagAsTestUser',
    //   title: 'Tag as Test User',
    //   description: 'Label the newly created user as a \'Test\' user to separate them from production users for easier management.',
    //   icon: <CheckCircleIcon sx={{ fontSize: 24, color: theme.palette.text.secondary }} />,
    //   status: taskStatus.tagAsTestUser
    // },
    // {
    //   key: 'setupDataSync',
    //   title: 'Setup Daily Data Sync',
    //   description: 'Configure a daily cron job to fetch incremental data from the source UUID and replicate it to this test user in the target project.',
    //   icon: <ScheduleIcon sx={{ fontSize: 24, color: theme.palette.text.secondary }} />,
    //   status: taskStatus.setupDataSync
    // }
  ];

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      bgcolor: theme.palette.background.default,
      py: 4
    }}>
      {/* Centered Container */}
      <Box sx={{ 
        maxWidth: '1000px',
        width: '65%',
        mx: 'auto',
        px: 6,
        '@media (max-width: 1200px)': {
          width: '80%',
          px: 4
        },
        '@media (max-width: 768px)': {
          width: '100%',
          px: 3
        }
      }}>
        {/* Header Section */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          mb: 4
        }}>
          <Typography 
            variant="h4" 
            sx={{ 
              fontWeight: 500, 
              color: 'text.primary',
              '@media (max-width: 768px)': {
                fontSize: '1.5rem'
              }
            }}
          >
            Creating Test User
          </Typography>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={handleBackToManageUsers}
            sx={{
              color: theme.palette.primary.main,
              borderColor: theme.palette.primary.main,
              px: 3,
              py: 1.5,
              fontSize: '1rem',
              fontWeight: 500,
              '&:hover': {
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.primary.contrastText,
                borderColor: theme.palette.primary.main,
              },
              '@media (max-width: 768px)': {
                px: 2,
                py: 1,
                fontSize: '0.875rem'
              }
            }}
          >
            Back to Manage Users
          </Button>
        </Box>

        {/* Main Content Container - Encapsulates everything except main heading */}
        <Box sx={{ 
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
          bgcolor: 'background.paper',
          p: 4
        }}>
          {/* Subheading Section */}
          <Box sx={{ mb: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <CheckCircleIcon sx={{ fontSize: 28, color: theme.palette.success.main }} />
              <Typography 
                variant="h6" 
                sx={{ 
                  fontWeight: 500, 
                  color: 'text.primary'
                }}
              >
                Creation Tasks
              </Typography>
            </Box>
            <Typography 
              variant="body1" 
              color="text.secondary"
              sx={{ 
                fontSize: '0.95rem',
                textAlign: 'left'
              }}
            >
              The system is creating the test users and setting up data synchronization.
            </Typography>
          </Box>

          {/* Progress Bar Section */}
          <Box sx={{ mb: 4 }}>
            <LinearProgress 
              variant="determinate" 
              value={progress.percentage} 
              sx={{ 
                height: 8, 
                borderRadius: 4,
                backgroundColor: theme.palette.grey[200],
                '& .MuiLinearProgress-bar': {
                  backgroundColor: theme.palette.primary.main,
                  borderRadius: 4
                }
              }} 
            />
            <Typography 
              variant="body1" 
              sx={{ 
                fontWeight: 500, 
                color: 'text.primary',
                mt: 1,
                fontSize: '0.95rem',
                textAlign: 'center'
              }}
            >
              {progress.completed} of {progress.total} tasks completed ({progress.percentage}%)
            </Typography>
          </Box>

          {/* Error Display */}
          {error && (
            <Box sx={{ mb: 3, p: 2, bgcolor: theme.palette.error.light + '20', border: `1px solid ${theme.palette.error.main}`, borderRadius: 2 }}>
              <Typography color="error">
                {error}
              </Typography>
            </Box>
          )}

          {/* Task List Section */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {tasks.map((task) => (
              <Card 
                key={task.key}
                sx={{ 
                  border: `2px solid ${getTaskBorderColor(task.status)}`,
                  borderRadius: 2,
                  bgcolor: getTaskBackgroundColor(task.status),
                  transition: 'all 0.3s ease'
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 3 }}>
                    {/* Task Icon */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 'fit-content' }}>
                      {getTaskIcon(task.status)}
                    </Box>

                    {/* Task Content */}
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          {task.icon}
                          <Typography 
                            variant="body1" 
                            sx={{ 
                              fontWeight: 500, 
                              color: 'text.primary',
                              fontSize: '0.95rem'
                            }}
                          >
                            {task.title}
                          </Typography>
                        </Box>
                        <Chip
                          label={getStatusText(task.status)}
                          size="small"
                          sx={{
                            backgroundColor: getStatusColor(task.status),
                            color: 'white',
                            fontWeight: 500,
                            minWidth: '90px',
                            fontSize: '0.75rem'
                          }}
                        />
                      </Box>
                      
                      <Typography 
                        variant="body2" 
                        color="text.secondary"
                        sx={{ 
                          mb: 2,
                          fontSize: '0.85rem',
                          textAlign: 'left'
                        }}
                      >
                        {task.key === 'assignShcCluster' && task.status === 'error' && shcClusterError
                          ? `SHC cluster assignment failed: ${shcClusterError}`
                          : task.description}
                      </Typography>

                      {/* Show UUID data for completed UUID assignment */}
                      {task.key === 'uuidAssignment' && task.status === 'completed' && uuidData && (
                        <Box sx={{ mt: 2, p: 2, bgcolor: theme.palette.success.light + '10', borderRadius: 1, border: `1px solid ${theme.palette.success.light}` }}>
                          <Typography variant="caption" sx={{ color: theme.palette.text.secondary, mb: 1, display: 'block' }}>
                            Generated UUIDs:
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {(() => {
                              // Handle different response structures for UUID display
                              let displayData: any[] = [];
                              
                              if (uuidData.results && Array.isArray(uuidData.results)) {
                                displayData = uuidData.results;
                              } else if (Array.isArray(uuidData)) {
                                displayData = uuidData;
                              } else if (uuidData.data && Array.isArray(uuidData.data)) {
                                displayData = uuidData.data;
                              } else if (uuidData.uuid) {
                                displayData = [uuidData];
                              }
                              
                              if (displayData.length > 0) {
                                return displayData.map((result: any, index: number) => (
                                  <Chip
                                    key={index}
                                    label={`${result.firstName || result.first_name || result.source_uuid || `User ${index + 1}`} - ${result.uuid}`}
                                    variant="outlined"
                                    size="small"
                                    sx={{
                                      fontFamily: 'monospace',
                                      fontSize: '0.75rem',
                                      backgroundColor: theme.palette.primary.light + '20',
                                      borderColor: theme.palette.primary.main,
                                      color: theme.palette.text.secondary,
                                      '& .MuiChip-label': {
                                        px: 1,
                                        py: 0.5
                                      }
                                    }}
                                  />
                                ));
                              } else {
                                return (
                                  <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                                    No UUID data available
                                  </Typography>
                                );
                              }
                            })()}
                          </Box>
                        </Box>
                      )}
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Box>

          {/* Completion Message - Show when UUID assignment is completed (SHC cluster assignment can fail) */}
          {taskStatus.uuidAssignment === 'completed' && (
            <Box sx={{ 
              p: 3, 
              bgcolor: 'background.paper', 
              textAlign: 'center', 
              borderRadius: 2, 
              border: '1px solid',
              borderColor: 'success.main',
              mt: 3
            }}>
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
                <CelebrationOutlinedIcon sx={{ fontSize: 40, color: 'success.main' }} />
              </Box>
              <Typography variant="h5" sx={{ fontWeight: 600, color: 'success.dark', mb: 1 }}>
                {totalUsers} Test Users Created!
              </Typography>
              <Typography variant="body1" color="success.dark" sx={{ mb: 2 }}>
                {taskStatus.assignShcCluster === 'completed' 
                  ? 'Users have been created and assigned to SHC clusters successfully.'
                  : 'Users have been created successfully. SHC cluster assignment failed but user(s) are ready for use.'
                }
              </Typography>
              <Button
                variant="contained"
                size="large"
                startIcon={<RemoveRedEyeOutlinedIcon />}
                onClick={handleViewCreatedUsers}
                sx={{
                  backgroundColor: theme.palette.primary.main,
                  color: theme.palette.primary.contrastText,
                  px: 4,
                  py: 1.5,
                  fontSize: '1rem',
                  fontWeight: 600,
                  width: '75%',
                  '&:hover': {
                    opacity: 0.9
                  }
                }}
              >
                View Created Users
              </Button>
            </Box>
          )}

          {/* Finish Button Section */}
          {taskStatus.uuidAssignment === 'completed' && (
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              mt: 3 
            }}>
              <Button
                variant="outlined"
                size="large"
                onClick={handleFinish}
                sx={{
                  color: theme.palette.primary.main,
                  borderColor: theme.palette.primary.main,
                  px: 20,
                  py: 2,
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  minWidth: '300px',
                  '&:hover': {
                    backgroundColor: theme.palette.primary.main,
                    color: theme.palette.primary.contrastText,
                    borderColor: theme.palette.primary.main,
                  }
                }}
              >
                Finish
              </Button>
            </Box>
          )}
        </Box>

        {/* Loading State */}
        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <CircularProgress size={40} />
          </Box>
        )}
      </Box>

      {/* User Details Modal */}
      <Dialog
        open={userDetailsModalOpen}
        onClose={handleCloseModal}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            maxHeight: '80vh'
          }
        }}
      >
        <DialogTitle sx={{ 
          borderBottom: `1px solid ${theme.palette.divider}`,
          pb: 2
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              User Details
            </Typography>
            <Button
              onClick={handleCloseModal}
              sx={{ minWidth: 'auto', p: 1 }}
            >
              ✕
            </Button>
          </Box>
        </DialogTitle>
        
        <DialogContent sx={{ pt: 3 }}>
          {userDetailsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : userDetails ? (
            <Box>
              <TableContainer component={Paper}>
                <Table sx={{ minWidth: 650 }} aria-label="user details table">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>First Name</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Last Name</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Primary Email</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Secondary Email</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Address</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>City</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>State</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Zip Code</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Rate Plan</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Meter Type</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Billing Cycle</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Persona Name</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Persona Description</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>DCX Physical Clusters</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Source UUID</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>UUID</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Source Environment</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Created At</TableCell>
                      {/* <TableCell sx={{ fontWeight: 600 }}>Updated At</TableCell> */}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Array.isArray(userDetails) ? (
                      // Handle multiple users
                      userDetails.map((user: any, index: number) => (
                        <TableRow key={index}>
                          <TableCell>{user.first_name }</TableCell>
                          <TableCell>{user.last_name }</TableCell>
                          <TableCell>{user.primary_email }</TableCell>
                          <TableCell>{user.secondary_email}</TableCell>
                          <TableCell>{user.address }</TableCell>
                          <TableCell>{user.city }</TableCell>
                          <TableCell>{user.state }</TableCell>
                          <TableCell>{user.zipcode }</TableCell>
                          <TableCell>{user.rate_plan}</TableCell>
                          <TableCell>
                            {user.meterDetails ? (
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {Object.keys(user.meterDetails).map((meterFuel, index) => (
                                  <Chip
                                    key={index}
                                    label={meterFuel}
                                    size="small"
                                    variant="outlined"
                                    sx={{ 
                                      fontSize: '0.7rem', 
                                      height: '20px',
                                      backgroundColor: theme.palette.primary.light + '20',
                                      borderColor: theme.palette.primary.main,
                                      color: theme.palette.primary.dark
                                    }}
                                  />
                                ))}
                              </Box>
                            ) : (
                              'N/A'
                            )}
                          </TableCell>
                          <TableCell>{user.billing_cycle }</TableCell>
                          <TableCell>{user.persona_name }</TableCell>
                          <TableCell>{user.persona_description }</TableCell>
                          <TableCell>
                            {user.dcx_clusters && user.dcx_clusters.length > 0 ? (
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {user.dcx_clusters.map((cluster: string, clusterIndex: number) => (
                                  <Chip
                                    key={clusterIndex}
                                    label={cluster}
                                    size="small"
                                    variant="outlined"
                                    sx={{ fontSize: '0.7rem', height: '20px' }}
                                  />
                                ))}
                              </Box>
                            ) : (
                              'N/A'
                            )}
                          </TableCell>
                          <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                            {user.source_uuid }
                          </TableCell>
                          <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                            {user.uuid || ''}
                          </TableCell>
                          <TableCell>{user.source_env }</TableCell>
                          <TableCell>
                            {user.created_at ? new Date(user.created_at).toLocaleString() : 'N/A'}
                          </TableCell>
                          {/* <TableCell>
                            {user.updated_at ? new Date(user.updated_at).toLocaleString() : 'N/A'}
                          </TableCell> */}
                        </TableRow>
                      ))
                    ) : (
                      // Handle single user (backward compatibility)
                      <TableRow>
                        <TableCell>{userDetails.first_name}</TableCell>
                        <TableCell>{userDetails.last_name }</TableCell>
                        <TableCell>{userDetails.primary_email}</TableCell>
                        <TableCell>{userDetails.secondary_email }</TableCell>
                        <TableCell>{userDetails.address }</TableCell>
                        <TableCell>{userDetails.city }</TableCell>
                        <TableCell>{userDetails.state }</TableCell>
                        <TableCell>{userDetails.zipcode}</TableCell>
                        <TableCell>{userDetails.rate_plan }</TableCell>
                        <TableCell>
                          {userDetails.meterDetails ? (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {Object.keys(userDetails.meterDetails).map((meterFuel, index) => (
                                <Chip
                                  key={index}
                                  label={meterFuel}
                                  size="small"
                                  variant="outlined"
                                  sx={{ 
                                    fontSize: '0.7rem', 
                                    height: '20px',
                                    backgroundColor: theme.palette.primary.light + '20',
                                    borderColor: theme.palette.primary.main,
                                    color: theme.palette.primary.dark
                                  }}
                                />
                              ))}
                            </Box>
                          ) : (
                            'N/A'
                          )}
                        </TableCell>
                        <TableCell>{userDetails.billing_cycle || 'N/A'}</TableCell>
                        <TableCell>{userDetails.persona_name || 'N/A'}</TableCell>
                        <TableCell>{userDetails.persona_description || 'N/A'}</TableCell>
                        <TableCell>
                          {userDetails.dcx_clusters && userDetails.dcx_clusters.length > 0 ? (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {userDetails.dcx_clusters.map((cluster: string, index: number) => (
                                <Chip
                                  key={index}
                                  label={cluster}
                                  size="small"
                                  variant="outlined"
                                  sx={{ fontSize: '0.7rem', height: '20px' }}
                                />
                              ))}
                            </Box>
                          ) : (
                            'N/A'
                          )}
                        </TableCell>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                          {userDetails.source_uuid }
                        </TableCell>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                          {userDetails.uuid || ''}
                        </TableCell>
                        <TableCell>{userDetails.source_env}</TableCell>
                        <TableCell>
                          {userDetails.created_at ? new Date(userDetails.created_at).toLocaleString() : 'N/A'}
                        </TableCell>
                        {/* <TableCell>
                          {userDetails.updated_at ? new Date(userDetails.updated_at).toLocaleString() : 'N/A'}
                        </TableCell> */}
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          ) : (
            <Box sx={{ textAlign: 'center', p: 4 }}>
              <Typography color="text.secondary">
                No user details found
              </Typography>
            </Box>
          )}
        </DialogContent>
        
        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button
            onClick={handleCloseModal}
            variant="contained"
            sx={{
              backgroundColor: theme.palette.primary.main,
              color: 'white',
              px: 4,
              py: 1.5,
              '&:hover': {
                backgroundColor: theme.palette.primary.dark,
              }
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}; 
